import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  Avatar
} from '@mui/material';
import {
  ThumbUp,
  ThumbDown,
  MoreVert,
  Verified,
  Edit,
  Delete
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { format } from 'date-fns';
import StarRating from './StarRating';
import reviewService, { Review } from '../../services/reviewService';

interface ReviewListProps {
  productId: number;
  refreshTrigger?: number;
}

const ReviewList: React.FC<ReviewListProps> = ({ productId, refreshTrigger }) => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('DESC');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await reviewService.getProductReviews(
        productId,
        page,
        10,
        sortBy,
        sortDirection
      );
      
      // Transform backend data to frontend format
      const transformedReviews = (response.reviews || []).map((review: any) => ({
        id: review.id,
        rating: review.rating,
        title: review.title,
        comment: review.comment,
        userName: review.user?.name || 'Unknown User',
        userId: review.user?.id,
        productId: productId,
        productTitle: '', // Not provided by public API
        verified: false, // Backend doesn't provide this field yet
        status: 'APPROVED', // Public API only returns approved reviews
        helpfulCount: review.helpful_count || 0,
        unhelpfulCount: 0, // Backend doesn't provide this field yet
        adminResponse: review.admin_response,
        adminResponseDate: review.admin_response_date,
        createdAt: review.created_at,
        updatedAt: review.updated_at,
        editable: false
      }));
      
      setReviews(transformedReviews);
      setTotalPages(response.pagination?.last_page || 1);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [productId, page, sortBy, sortDirection]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews, refreshTrigger]);

  const handleVoteHelpful = async (reviewId: number) => {
    if (!user) {
      // Show login prompt
      return;
    }
    try {
      const updatedReview = await reviewService.voteHelpful(reviewId);
      setReviews(reviews.map(r => r.id === reviewId ? updatedReview : r));
    } catch (error) {
    }
  };

  const handleVoteUnhelpful = async (reviewId: number) => {
    if (!user) {
      // Show login prompt
      return;
    }
    try {
      const updatedReview = await reviewService.voteUnhelpful(reviewId);
      setReviews(reviews.map(r => r.id === reviewId ? updatedReview : r));
    } catch (error) {
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, review: Review) => {
    setAnchorEl(event.currentTarget);
    setSelectedReview(review);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReview(null);
  };

  const handleDelete = async () => {
    if (!selectedReview) return;
    
    if (window.confirm(t('reviews.confirmDelete'))) {
      try {
        await reviewService.deleteReview(selectedReview.id);
        await loadReviews();
      } catch (error) {
      }
    }
    handleMenuClose();
  };

  const getSortOptions = () => [
    { value: 'createdAt-DESC', label: t('reviews.sort.newest') },
    { value: 'createdAt-ASC', label: t('reviews.sort.oldest') },
    { value: 'rating-DESC', label: t('reviews.sort.highestRating') },
    { value: 'rating-ASC', label: t('reviews.sort.lowestRating') },
    { value: 'helpfulCount-DESC', label: t('reviews.sort.mostHelpful') }
  ];

  const handleSortChange = (value: string) => {
    const [field, direction] = value.split('-');
    setSortBy(field);
    setSortDirection(direction);
    setPage(0);
  };

  if (loading && reviews.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!loading && reviews.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          {t('reviews.noReviews')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {t('reviews.customerReviews')}
        </Typography>
        <Box>
          <select
            value={`${sortBy}-${sortDirection}`}
            onChange={(e) => handleSortChange(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              fontSize: '14px'
            }}
          >
            {getSortOptions().map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {reviews.map((review) => (
        <Paper key={review.id} sx={{ p: 2, mb: 2 }} elevation={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                  {review.userName?.charAt(0) || '?'}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2">
                      {review.userName}
                    </Typography>
                    {review.verified && (
                      <Chip
                        icon={<Verified />}
                        label={t('reviews.verifiedPurchase')}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {review.createdAt ? format(new Date(review.createdAt), 'dd MMM yyyy') : 'N/A'}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <StarRating rating={review.rating} size="small" />
                <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 'bold' }}>
                  {review.title}
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ mb: 2 }}>
                {review.comment}
              </Typography>

              {review.adminResponse && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('reviews.storeResponse')}:
                  </Typography>
                  <Typography variant="body2">
                    {review.adminResponse}
                  </Typography>
                  {review.adminResponseDate && (
                    <Typography variant="caption" color="text.secondary">
                      {review.adminResponseDate ? format(new Date(review.adminResponseDate), 'dd MMM yyyy') : ''}
                    </Typography>
                  )}
                </Alert>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  size="small"
                  startIcon={<ThumbUp />}
                  onClick={() => handleVoteHelpful(review.id)}
                  disabled={!user}
                >
                  {t('reviews.helpful')} ({review.helpfulCount})
                </Button>
                <Button
                  size="small"
                  startIcon={<ThumbDown />}
                  onClick={() => handleVoteUnhelpful(review.id)}
                  disabled={!user}
                >
                  {t('reviews.notHelpful')} ({review.unhelpfulCount})
                </Button>
              </Box>
            </Box>

            {user && (user.id === review.userId || user.role === 'ADMIN') && (
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, review)}
              >
                <MoreVert />
              </IconButton>
            )}
          </Box>
        </Paper>
      ))}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={(_, value) => setPage(value - 1)}
            color="primary"
          />
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedReview && user?.id === selectedReview.userId && selectedReview.editable && (
          <MenuItem onClick={handleMenuClose}>
            <Edit sx={{ mr: 1 }} fontSize="small" />
            {t('common.edit')}
          </MenuItem>
        )}
        <MenuItem onClick={handleDelete}>
          <Delete sx={{ mr: 1 }} fontSize="small" />
          {t('common.delete')}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ReviewList;